import React, { useState, useEffect, useRef, useContext } from "react";
import { StyleSheet, Dimensions, ScrollView, Text, TouchableOpacity } from "react-native";
import { FAB } from "react-native-paper";
import { View } from "../../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import { default as Modal } from "../../../components/custom/Modal";
import Spinner from "../../../components/shared/Spinner";
import useColorScheme from "../../../hooks/useColorScheme";
import { getAllProducts } from "../../../services/productService";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  headersProduct,
  mediumDeviceHeadersProduct as mdProduct,
  smallDeviceHeadersProduct as smProduct,
} from "../utils/productHeaders";
import { productModel } from "../../../models/reportsModel";
import { validSignedURL } from "../../../utils/validateSignedUrl";
import { handleTableLoader } from "../../../utils/common";
import { useSelector } from "react-redux";
import { userClaimInformation } from "../../../models/usersModel";
import { useIsFocused } from "@react-navigation/native";
import { sortColumns } from "../../../utils/sortColumns";
import { filterBasedOnAnyKey } from "../../../utils/searchInArrays";
import { LabelBannerWithSearchTags } from "../../../components/custom/Banners/LabelBannerWithSearchTags";
import { ReportsDispatcher } from "../../../redux/dispatcher/reportsDispatcher";
import { actionTypesReports } from "../../../redux/constants/actionTypesReports";
import { formatDate } from "../../../utils/dateUtility";

export default function ProductsReportScreen({ route, navigation }: any) {
  const colorScheme = useColorScheme();
  const isFocused = useIsFocused();
  const listRef = useRef<ScrollView>(null);

  const [productsPageLevelList, setProductsPageLevelList] = useState<
    Array<productModel>
  >([]);

  const [isSearched, setIsSearched] = useState<boolean>(false);

  const { userInfo } = useSelector((state: any) => state.configurations);
  const {
    productReportList,
    isProductReportListLoaded,
    productReportFilterCategoryList,
    lastRefreshProductReport,
  } = useSelector((state: any) => state.reports);

  const [selected, setSelected] = useState<productModel>({} as productModel);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headersProduct
    );
  const [searchedText, setSearchedText] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [lastRefresh, setLastRefresh] = useState<string>("");
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [, setFabOpen] = useState<Record<string, boolean>>({
    open: false,
  });
  const [fabPosition] = useState<number>(320);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [isImageLoading, setImageLoading] = useState<boolean>(true);
  const [contentOffSet, setContentOffSet] = useState<number>(0);

  const [_isDisplayViewOn, _setIsDisplayViewOn] = useState(false);
  const [_filterCategory, _setFilterCategory] = useState<string>("all");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);

  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedProductsReportList: Array<productModel> = sortColumns(
      productsPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setProductsPageLevelList(_sortedProductsReportList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      setSearchedText(searchText);

      const filteredListBasedOnSearchOrCondition: Array<productModel> =
        filterBasedOnAnyKey(productReportList, searchText);

      const _filterProductByCategory = filterProductByCategory(
        filteredListBasedOnSearchOrCondition,
        _filterCategory,
        selectedCategories,
      );

      //filter based on tags or display switch
      setProductsPageLevelList(
        _isDisplayViewOn ? _filterProductByCategory : _filterProductByCategory
      );
    } else {
      setSearchedText("");
    }

    fadeTableLoader();
  };

  const onFabOpenChange = ({ open }: { open: boolean }) => setFabOpen({ open });

  const filterProductByCategory = (
    _productsReport: Array<productModel>,
    _filterCategory: string,
    categoriesArr?: string[],
  ) => {
    const activeCategories = categoriesArr && categoriesArr.length > 0 ? categoriesArr : (_filterCategory !== "all" ? [_filterCategory] : []);
    if (activeCategories.length === 0) return _productsReport;

    const matchesCategory = (product: productModel, cat: string): boolean => {
      switch (cat) {
        case "produce":
          return [101, 102, 103, 104, 106].includes(product.ItmsGrpCod);
        case "berry":
          return product.ItmsGrpCod === 100;
        case "meat":
          return product.ItmsGrpCod === 110;
        case "meals":
          return [111, 116].includes(product.ItmsGrpCod);
        case "salad":
          return product.ItmsGrpCod === 107;
        case "bakery":
          return product.ItmsGrpCod === 108;
        default:
          return true;
      }
    };

    return _productsReport.filter((product: productModel) =>
      activeCategories.some((cat) => matchesCategory(product, cat))
    );
  };

  useEffect(() => {
    setLastRefresh(lastRefreshProductReport);
  }, [lastRefreshProductReport]);

  useEffect(() => {
    let isMounted: boolean = true;
    const _userInfo = userInfo as userClaimInformation;

    if (isMounted) {
      //To restrict fetching data from endpoint, if data is available in store
      if (productsPageLevelList.length === 0 || isRefresh) {
        displayTableLoader();

        //set filtering by category & display by carton to default
        _setIsDisplayViewOn(false);
        _setFilterCategory("all");
        setSearchedText("");

        //set last refresh date
        const formattedLastRefreshDate = formatDate(new Date(), "short", true);
        ReportsDispatcher(
          actionTypesReports.LastRefreshProductReport,
          formattedLastRefreshDate
        );

        getAllProducts(_userInfo.team, _userInfo.zone_info, tableHeaders);

        fadeTableLoader();
      }
    }

    setTableHeaders(
      isLargeDevice ? headersProduct : isMobileDevice ? smProduct : mdProduct
    );

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isFocused, isRefresh]);

  useEffect(() => {
    setImageLoading(true);
  }, [modalVisible]);

  useEffect(() => {
    displayTableLoader();

    const _productsReport = _isDisplayViewOn
      ? productsPageLevelList
      : (productReportList as Array<productModel>);
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_productsReport, searchedText)
        : _productsReport;

    if (_isDisplayViewOn) {
      setProductsPageLevelList(filteredListBasedOnSearchOrCondition);
    } else {
      setProductsPageLevelList(
        filterProductByCategory(
          filteredListBasedOnSearchOrCondition,
          _filterCategory,
          selectedCategories,
        )
      );
    }

    fadeTableLoader();
  }, [_isDisplayViewOn]);

  useEffect(() => {
    displayTableLoader();

    const _productsReport = productReportList as Array<productModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_productsReport, searchedText)
        : _productsReport;

    if (_isDisplayViewOn) {
      setProductsPageLevelList(
        filterProductByCategory(
          filteredListBasedOnSearchOrCondition,
          _filterCategory,
          selectedCategories,
        )
      );
    } else {
      setProductsPageLevelList(
        filterProductByCategory(
          filteredListBasedOnSearchOrCondition,
          _filterCategory,
          selectedCategories,
        )
      );
    }

    fadeTableLoader();
  }, [_filterCategory, selectedCategories]);

  useEffect(() => {
    displayTableLoader();

    const _productsReport = productReportList as Array<productModel>;
    const filteredListBasedOnSearchOrCondition =
      searchedText.trim().length > 0
        ? filterBasedOnAnyKey(_productsReport, searchedText)
        : _productsReport;

    if (_filterCategory === "all" && !_isDisplayViewOn) {
      setProductsPageLevelList(filteredListBasedOnSearchOrCondition);
    }

    fadeTableLoader();
  }, [productReportList, _isDisplayViewOn, _filterCategory === "all" && selectedCategories.length === 0]);

  useEffect(() => {
    validSignedURL(
      selected.ItemCode || "",
      "label",
      setImageLoading,
      setSelectedImage
    );
  }, [selected]);

  const ModalContent = () => {
    const _excludedKeys = [
      "ItemCode",
      "PerCtns",
      "PerPallet",
      "FrgnName",
      "CardName",
    ];

    const _orderedKeys = [
      "ItemCode",
      "PerCtns",
      "PerPallet",
      "FrgnName",
      "CardName",
    ];

    const _specialLabels = {
      PerPallet: {
        oldLabel: "PerPallet",
        newLabel: "Total Per Pallet",
        newValue: (selected.PerPallet * selected.PerCtns) as unknown as string,
        supportingValue: ((selected.PerPallet * selected.PerCtns) /
          selected.PerCtns) as unknown as string,
      },
      ItmsGrpCod: {
        oldLabel: "ItmsGrpCod",
        newLabel: "Display",
        newValue: _isDisplayViewOn ? "Per CARTON" : "Per PIECE",
      },
    };

    return (
      <Modal.ModalContent
        title={selected.ItemCode || ""}
        subTitle={selected.FrgnName}
        icon="folder"
        uri={selectedImage}
        loading={isImageLoading}
        childern={
          <Modal.ModalBody
            listData={selected}
            excludedKeys={_excludedKeys}
            renameList={headersProduct}
            orderedKeys={_orderedKeys}
            specialLabels={_specialLabels}
          />
        }
      />
    );
  };

  return (
    <ScrollView
      style={{ backgroundColor: colorScheme === "light" ? "white" : "white" }}
      onScroll={handleScroll}
      ref={listRef}
    >
      <View
        style={[
          styles.container,
          { backgroundColor: colorScheme === "light" ? "#f2f2f2" : "#f2f2f2" },
        ]}
      >
        <View
          style={[
            styles.containerContent,
            {
              width: isLargeDevice ? "100%" : isMediumDevice ? "100%" : "100%",
            },
          ]}
        >
          {isProductReportListLoaded ? (
            <View>
              <FAB
                icon="arrow-up"
                style={[
                  styles.fab,
                  {
                    top:
                      contentOffSet +
                      Dimensions.get("window").height -
                      fabPosition,
                  },
                ]}
                onPress={() =>
                  listRef.current?.scrollTo({ x: 0, y: 0, animated: true })
                }
                small
              />
              <LabelBannerWithSearchBar
                titleName={`Total Items: ${productsPageLevelList.length}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}
                dataToExport={[]}
                fileName="productReportScreen"
                sheetName="Product Report Screen"
              />
              <View style={productFilterStyles.renderFilterStyles}>
                {(isSmallDevice || isMobileDevice) ? (
                  <View style={productFilterStyles.mobileFilterWrapper}>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                    >
                      <TouchableOpacity
                        style={[
                          productFilterStyles.mobileDropdownButton,
                          mobileFilterOpen && productFilterStyles.mobileDropdownButtonActive,
                        ]}
                        onPress={() => setMobileFilterOpen(!mobileFilterOpen)}
                      >
                        <Text style={productFilterStyles.mobileDropdownLabel}>Filter: </Text>
                        <Text style={productFilterStyles.mobileDropdownValue}>
                          {selectedCategories.length === 0
                            ? "All"
                            : selectedCategories.length === 1
                              ? selectedCategories[0].charAt(0).toUpperCase() + selectedCategories[0].slice(1)
                              : `${selectedCategories.length} selected`}
                        </Text>
                        <Text style={productFilterStyles.mobileDropdownArrow}>{mobileFilterOpen ? "▲" : "▼"}</Text>
                      </TouchableOpacity>
                    </ScrollView>
                    {mobileFilterOpen && (
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={productFilterStyles.mobileChipRow}
                      >
                        {productReportFilterCategoryList.map((category: string, index: number) => {
                          const isActive = category === "all"
                            ? selectedCategories.length === 0
                            : selectedCategories.includes(category);
                          return (
                            <TouchableOpacity
                              key={index}
                              style={[
                                productFilterStyles.filterButton,
                                isActive && productFilterStyles.activeFilterButton,
                              ]}
                              onPress={() => {
                                if (category === "all") {
                                  setSelectedCategories([]);
                                  _setFilterCategory("all");
                                } else {
                                  const newCats = selectedCategories.includes(category)
                                    ? selectedCategories.filter((c) => c !== category)
                                    : [...selectedCategories, category];
                                  setSelectedCategories(newCats);
                                  _setFilterCategory(newCats.length === 0 ? "all" : newCats.join(","));
                                }
                              }}
                            >
                              <Text
                                style={
                                  isActive
                                    ? productFilterStyles.activeFilterText
                                    : productFilterStyles.filterText
                                }
                              >
                                {category === "all" ? "All" : category.charAt(0).toUpperCase() + category.slice(1)}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    )}
                  </View>
                ) : (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={productFilterStyles.filterContainer}
                  >
                    <Text style={productFilterStyles.quickFilterText}>Quick Filter</Text>
                    {productReportFilterCategoryList.map((category: string, index: number) => {
                      const isActive = category === "all"
                        ? selectedCategories.length === 0
                        : selectedCategories.includes(category);
                      return (
                        <TouchableOpacity
                          key={index}
                          style={[
                            productFilterStyles.filterButton,
                            isActive && productFilterStyles.activeFilterButton,
                          ]}
                          onPress={() => {
                            if (category === "all") {
                              setSelectedCategories([]);
                              _setFilterCategory("all");
                            } else {
                              const newCats = selectedCategories.includes(category)
                                ? selectedCategories.filter((c) => c !== category)
                                : [...selectedCategories, category];
                              setSelectedCategories(newCats);
                              _setFilterCategory(newCats.length === 0 ? "all" : newCats.join(","));
                            }
                          }}
                        >
                          <Text
                            style={
                              isActive
                                ? productFilterStyles.activeFilterText
                                : productFilterStyles.filterText
                            }
                          >
                            {category === "all" ? "All" : category.charAt(0).toUpperCase() + category.slice(1)}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                )}
              </View>
              <DataTableWithPagination
                headers={tableHeaders}
                rows={productsPageLevelList}
                itemsPerPage={20}
                useModal={true}
                isDataLoading={isSearched}
                isRowsExpandable={false}
                setModalVisible={setModalVisible}
                setSelected={setSelected}
                isDetail={true}
                handleRowClick={() => { }}
                handleDeleteRow={() => { }}
                handleSortColumn={onHandleSortColumn}
              />
            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>
      <Modal.ModalContainer
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        childern={ModalContent}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    justifySelf: "flex-start",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
});

const productFilterStyles = StyleSheet.create({
  renderFilterStyles: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
    marginTop: 8,
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 4,
    gap: 8,
    alignItems: "center",
  },
  quickFilterText: {
    display: "flex",
    alignItems: "center",
    marginRight: 10,
    fontWeight: "600",
    fontSize: 13,
    color: "#374151",
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  activeFilterButton: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterText: {
    fontSize: 13,
    fontWeight: "500",
    color: "#475569",
  },
  activeFilterText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
  },
  mobileFilterWrapper: {
    width: "100%",
    marginBottom: 4,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
});
